(function () {
  if (customElements.get('fp-hero-slideshow')) return;

  const ARM_EVENTS = ['scroll', 'pointerdown', 'keydown'];

  class FpHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      this.slides = Array.from(this.querySelectorAll('[data-fp-slide]'));
      this.copies = Array.from(this.querySelectorAll('[data-fp-copy]'));
      this.dots = Array.from(this.querySelectorAll('[data-fp-dot]'));
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.timer = 0;
      this.safetyTimer = 0;
      this.interval = Math.max(3, Number(this.dataset.interval) || 6) * 1000;
      this.autoplay = this.dataset.autoplay !== 'false';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.armed = false;

      this.querySelector('[data-fp-prev]')?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
      this.querySelector('[data-fp-next]')?.addEventListener('click', () => this.goTo(this.index + 1), { signal });
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i), { signal }));
      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;
        // The poster stays up until real frames arrive (see fp-hero.css).
        const sync = () => slide.classList.toggle('is-playing', !video.paused && !video.ended);
        video.addEventListener('playing', sync, { signal });
        video.addEventListener('pause', sync, { signal });
        video.addEventListener('ended', sync, { signal });
      });
      // Phones: swipe the photo sideways to change slide (the arrows are
      // hidden there to keep the photo clear).
      const media = this.querySelector('.fp-hero__media');
      let touchX = null;
      let touchY = null;
      media?.addEventListener('touchstart', (event) => {
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
      }, { passive: true, signal });
      media?.addEventListener('touchend', (event) => {
        if (touchX === null) return;
        const dx = event.changedTouches[0].clientX - touchX;
        const dy = event.changedTouches[0].clientY - touchY;
        touchX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        this.goTo(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true, signal });
      this.addEventListener('pointerenter', () => this.stop(), { signal });
      this.addEventListener('pointerleave', () => this.start(), { signal });
      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.start();
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
      }, { signal });

      this.arm = () => {
        this.armed = true;
        ARM_EVENTS.forEach((type) => window.removeEventListener(type, this.arm));
        if (!this.matches(':hover, :focus-within')) this.start();
      };
      ARM_EVENTS.forEach((type) => window.addEventListener(type, this.arm, { passive: true, signal }));
      const modeChanged = () => this.goTo(this.index);
      this.motionQuery.addEventListener('change', modeChanged, { signal });
      this.mobileQuery.addEventListener('change', modeChanged, { signal });
      this.goTo(this.index);
    }

    // Video plays and slides turn on their own, phones included. Reduced
    // motion or data saver keep the still images and leave the slides put.
    get automaticMedia() {
      return !this.motionQuery.matches && !navigator.connection?.saveData;
    }

    #fillImage(slide) {
      const img = slide?.querySelector('img[data-src], img[data-srcset]');
      if (!img) return;
      img.loading = 'eager';
      if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
      if (img.dataset.src) img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    }

    disconnectedCallback() {
      this.stop();
      window.clearTimeout(this.safetyTimer);
      this.lifecycle?.abort();
      this.videoAbort?.abort();
      this.slides?.forEach((slide) => slide.querySelector('video')?.pause());
    }

    start() {
      this.stop();
      if (!this.autoplay || !this.automaticMedia || !this.armed || this.slides.length < 2) return;
      if (this.slides[this.index]?.querySelector('video')) return;
      this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = 0;
    }

    goTo(next) {
      if (!this.slides.length) return;
      window.clearTimeout(this.safetyTimer);
      this.videoAbort?.abort();
      const index = ((next % this.slides.length) + this.slides.length) % this.slides.length;
      this.index = index;
      // Only the chosen slide needs an image; hidden slides stay unfetched.
      this.#fillImage(this.slides[index]);
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.inert = !active;
        this.#setVideo(slide, active);
      });
      this.copies.forEach((copy, i) => {
        const active = i === index;
        copy.classList.toggle('is-active', active);
        copy.setAttribute('aria-hidden', active ? 'false' : 'true');
        copy.inert = !active;
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      this.start();
    }

    #attachSources(video) {
      if (video.dataset.fpSourcesAttached) return;
      const sources = video.querySelectorAll('source[data-src]');
      if (!sources.length) return;
      video.dataset.fpSourcesAttached = '1';
      sources.forEach((source) => {
        source.setAttribute('src', source.dataset.src || '');
        source.removeAttribute('data-src');
      });
      video.load();
    }

    #setVideo(slide, active) {
      const video = slide.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;
      if (!active || !this.automaticMedia) {
        video.pause();
        return;
      }
      this.videoAbort?.abort();
      this.videoAbort = new AbortController();
      const { signal } = this.videoAbort;
      const advance = this.autoplay && this.automaticMedia && this.slides.length > 1;
      video.loop = !advance;
      const play = () => {
        if (!this.isConnected || signal.aborted || this.slides[this.index] !== slide) return;
        this.#attachSources(video);
        video.play().catch(() => {});
      };
      // Video waits until the page has loaded, so it never competes with the
      // poster and the rest of the page for the connection.
      if (document.readyState === 'complete') play();
      else window.addEventListener('load', play, { once: true, signal });
      if (!advance) return;
      const next = () => this.goTo(this.index + 1);
      video.addEventListener('ended', next, { signal, once: true });
      video.addEventListener('error', () => {
        this.safetyTimer = window.setTimeout(next, this.interval);
      }, { signal, once: true });
      video.addEventListener('playing', () => {
        const duration = Number.isFinite(video.duration) ? video.duration * 1000 : this.interval * 2;
        this.safetyTimer = window.setTimeout(next, duration + 600);
      }, { signal, once: true });
    }
  }

  customElements.define('fp-hero-slideshow', FpHeroSlideshow);
})();
